import { Injectable, Signal, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { DotCMSGraphQLParams, DotCMSPageAsset, DotCMSPageRequestParams } from '@dotcms/types';
import { getUVEState } from '@dotcms/uve';
import { concatLatestFrom } from '@ngrx/operators';
import { Store, select } from '@ngrx/store';
import { isEqual } from 'lodash-es';
import {
  Observable,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  withLatestFrom,
} from 'rxjs';

import { AppFacade } from 'ish-core/facades/app.facade';
import { DotCmsUveConfig } from 'ish-core/models/dot-cms-uve/dot-cms-uve-config.model';
import { DeviceType } from 'ish-core/models/viewtype/viewtype.types';
import {
  applyConfiguration,
  getCurrentDotCmsApiConfiguration,
  getCurrentDotCmsFooterIdentifier,
  getCurrentDotCmsPersona,
  getCurrentLocale,
  getDotCmsLanguageConfiguration,
} from 'ish-core/store/core/configuration';
import { selectCleanUrl, selectPreviousUrl, selectRouter } from 'ish-core/store/core/router/router.selectors';
import { setIsTransparentHeader } from 'ish-core/store/core/viewconf';
import { whenTruthy } from 'ish-core/utils/operators';

import { ExtraContent, VideoContentlet } from '../models/dot-cms-uve-contentlet.model';
import { PageState } from '../models/dot-cms-uve.model';
import { DotCmsUveEditablePageService } from '../services/dot-cms-uve-editable-page/dot-cms-uve-editable-page.service';
import {
  BASE_EXTRA_QUERIES,
  createNavigationQuery,
  createPageQuery,
  footerQuery,
  fragmentNav,
  fragmentPage,
} from '../shared/queries/queries';

type DotCMSPage = {
  pageAsset: DotCMSPageAsset;
  content: ExtraContent;
};

interface Deps {
  personaId: string | undefined;
  languageId: number | undefined;
  footerIdentifier: string | undefined;
  url: string | undefined;
}

/* eslint-disable @typescript-eslint/member-ordering */
@Injectable({ providedIn: 'root' })
export class DotCmsUveFacade {
  private store = inject(Store);
  private appFacade = inject(AppFacade);
  private router = inject(Router);

  private editablePageService = inject<DotCmsUveEditablePageService<DotCMSPage>>(DotCmsUveEditablePageService);

  // State and signals
  private readonly _pageState = signal<PageState<DotCMSPage> | null>(null);
  readonly pageState = computed(() => this._pageState());
  private initializedOnce = false;
  private lastDeps: Deps | null = null;
  private currentPageSignal = signal<Signal<PageState<DotCMSPage> | null> | null>(null);
  private readonly _pageLoading = signal<boolean>(false);
  readonly pageLoading = computed(() => this._pageLoading());
  readonly $pageUrl = toSignal(
    this.store.pipe(
      select(selectCleanUrl),
      filter(url => url !== undefined),
      distinctUntilChanged(isEqual),
      map(url => (url === '/home' ? '/index' : (url.replace(/^\/page(\/.*)$/, '$1') as string)))
    )
  );

  private readonly currentLocale$ = this.store.pipe(select(getCurrentLocale), whenTruthy());

  private readonly languageId$ = this.currentLocale$.pipe(
    concatLatestFrom(() => this.store.pipe(select(getDotCmsLanguageConfiguration))),
    map(([locale, config]) => config?.[locale])
  );

  private readonly deps$ = combineLatest([
    this.store.pipe(select(getCurrentDotCmsPersona), distinctUntilChanged()),
    this.languageId$.pipe(distinctUntilChanged()),
    this.store.pipe(select(getCurrentDotCmsFooterIdentifier), distinctUntilChanged()),
    this.store.pipe(select(selectCleanUrl), distinctUntilChanged()),
  ]).pipe(
    debounceTime(200),
    distinctUntilChanged(isEqual),
    shareReplay(1),
    map(([personaId, languageId, footerIdentifier, url]) => {
      this.applyLanguageConfiguration();
      return {
        personaId,
        languageId,
        footerIdentifier,
        url,
      };
    })
  );

  private readonly depsSignal = toSignal(this.deps$, { initialValue: null });

  constructor() {
    // Effect to handle dependency changes and create pageSignal
    effect(
      () => {
        const deps = this.depsSignal();
        if (!deps || (this.lastDeps && isEqual(this.lastDeps, deps))) {
          this.store.dispatch(
            setIsTransparentHeader({
              isTransparentHeader: this.pageState()?.pageResponse?.content?.extraPageProps?.transparentHeader ?? false,
            })
          );
          return;
        }
        if ((!this.initializedOnce || deps.url?.includes('/page') || deps.url === '/home') && deps.url) {
          this.initializedOnce = true;
          this.lastDeps = deps;
          this._pageLoading.set(true);
          this.initializePageSignal(deps);
        }
      },
      { allowSignalWrites: true }
    );

    // Effect to update _pageState
    effect(
      () => {
        const pageSignal = this.currentPageSignal()?.();
        if (!pageSignal) {
          return;
        }

        try {
          this._pageState.set(pageSignal);
          if (
            pageSignal.status === 'error' ||
            (pageSignal.pageResponse?.pageAsset && pageSignal.status !== 'loading')
          ) {
            this._pageLoading.set(false);
          }
        } catch (error) {
          console.error('Failed to update page state:', error);
          this._pageState.set({ status: 'error', error: undefined });
          this._pageLoading.set(false);
        }
      },
      { allowSignalWrites: true }
    );
  }

  private initializePageSignal(deps: Deps): void {
    const graphqlQueries = this.buildGraphQLQueries(deps);
    const initConfig: DotCMSPageRequestParams = {
      graphql: graphqlQueries,
      ...(deps.personaId && { personaId: deps.personaId }),
      ...(deps.languageId && { languageId: deps.languageId }),
    };

    try {
      const rawPageSignal = this.editablePageService.initializePage(initConfig, deps.url!);
      let lastState: PageState<DotCMSPage> | null = null;
      const deduplicatedPageSignal = computed(() => {
        const state = rawPageSignal();
        if (!state || state.status === 'loading') {
          return null;
        }
        if (lastState && isEqual(this.normalizeState(lastState), this.normalizeState(state))) {
          return null;
        }
        lastState = state;
        return this.mergePageState(state);
      });

      this.currentPageSignal.set(deduplicatedPageSignal);
    } catch (error) {
      console.error('Failed to initialize page signal:', error);
      this._pageState.set({ status: 'error', error: undefined });
      this._pageLoading.set(false);
    }
  }

  private buildGraphQLQueries(deps: Deps): DotCMSGraphQLParams {
    const currentPageState = this._pageState();
    //@ts-ignore
    const hasFooter = currentPageState?.pageResponse?.content?.footer !== undefined;
    const hasHeaderNavigation = currentPageState?.pageResponse?.content?.navigation !== undefined;
    const isEditMode = getUVEState()?.mode === 'EDIT_MODE';
    const graphqlQueries: DotCMSGraphQLParams = { ...BASE_EXTRA_QUERIES };

    if (deps.footerIdentifier && (isEditMode || !hasFooter)) {
      graphqlQueries.content = {
        ...graphqlQueries.content,
        footer: footerQuery(deps.footerIdentifier),
      };
    }

    if (!hasHeaderNavigation) {
      graphqlQueries.content = {
        ...graphqlQueries.content,
        navigation: createNavigationQuery(5),
      };
      graphqlQueries.fragments = [...(graphqlQueries.fragments || []), fragmentNav];
    }

    let pagePath = deps.url;

    if (pagePath === '/home') {
      pagePath = '/index';
    } else if (pagePath.startsWith('/page/')) {
      // Extract everything after "/page/"
      const subPath = pagePath.split('/page/')[1];
      pagePath = `/${subPath}`; // optional leading slash
    }

    graphqlQueries.content = {
      ...graphqlQueries.content,
      extraPageProps: createPageQuery(pagePath), // or dynamic path
    };

    graphqlQueries.fragments = [...(graphqlQueries.fragments || []), fragmentPage];

    return graphqlQueries;
  }

  private mergePageState(newState: PageState<DotCMSPage>): PageState<DotCMSPage> {
    const currentPageState = this._pageState();
    return {
      ...newState,
      pageResponse: {
        ...newState.pageResponse,
        //@ts-ignore
        content: {
          ...(currentPageState?.pageResponse?.content || {}),
          //@ts-ignore
          ...(newState.pageResponse?.content?.footer !== undefined
            ? //@ts-ignore
              { footer: newState.pageResponse.content.footer }
            : {}),
          ...(newState.pageResponse?.content?.navigation !== undefined
            ? { navigation: newState.pageResponse.content.navigation }
            : {}),
          ...(newState.pageResponse?.content?.extraPageProps !== undefined
            ? { extraPageProps: newState.pageResponse.content.extraPageProps }
            : {}),
        },
      },
    };
  }

  private normalizeState(state: PageState<DotCMSPage> | null) {
    if (!state) {
      return null;
    }
    return {
      ...state,
      pageResponse: state.pageResponse
        ? {
            ...state.pageResponse,
            pageAsset: state.pageResponse.pageAsset
              ? {
                  ...state.pageResponse.pageAsset,
                  page: state.pageResponse.pageAsset.page ? { ...state.pageResponse.pageAsset.page } : undefined,
                }
              : undefined,
            graphql: state.pageResponse.graphql ? { ...state.pageResponse.graphql } : undefined,
          }
        : undefined,
    };
  }

  private applyLanguageConfiguration(): void {
    let languageConfig: DotCmsUveConfig;
    let languageId!: number;
    this.store
      .pipe(
        select(selectRouter),
        withLatestFrom(this.store.pipe(select(getDotCmsLanguageConfiguration))),
        concatLatestFrom(() => this.store.pipe(select(getCurrentLocale)))
      )
      .subscribe(([[routerState, config], locale]) => {
        //state => state.router?.state?.queryParams?.language_id
        languageId = routerState?.state?.queryParams?.language_id;
        languageConfig = {
          languageId: config,
        };

        const languageMap = languageConfig;

        const matchedLocale = Object.keys(languageMap.languageId).find(
          value => +languageConfig?.languageId?.[value] === +languageId
        );
        if (matchedLocale) {
          this.store.dispatch(
            applyConfiguration({
              lang: matchedLocale ?? locale,
            })
          );
        }
      });
  }

  setTransparentHeader() {
    this.store.dispatch(
      setIsTransparentHeader({
        isTransparentHeader: this.pageState()?.pageResponse?.content?.extraPageProps?.transparentHeader ?? false,
      })
    );
  }

  getDotCmsHost$ = this.store.pipe(select(getCurrentDotCmsApiConfiguration)).pipe(map(config => config.host));

  getVideoHeight(contentlet: VideoContentlet): Observable<string> {
    return this.appFacade.deviceType$.pipe(
      map(deviceType => this.generateVideoHeightUsingDeviceType(contentlet, deviceType))
    );
  }

  private generateVideoHeightUsingDeviceType(contentlet: VideoContentlet, deviceType: DeviceType) {
    return deviceType === 'desktop'
      ? contentlet.heightlg
      : deviceType === 'tablet'
      ? contentlet.heightmd
      : deviceType === 'mobile'
      ? contentlet.heightsm
      : '';
  }

  getVideoClasses(contentlet: VideoContentlet): string {
    if (contentlet?.maxWidth) {
      return `${contentlet.cssClass}` + ` video-max-width`;
    }

    return contentlet.cssClass;
  }

  getPreviousUrl(): Observable<string> {
    return this.store.pipe(select(selectPreviousUrl));
  }

  currentPageUrl(): Observable<string> {
    return this.store.pipe(select(selectCleanUrl));
  }
  navigateTo(url: string) {
    this.router.navigate([url]);
  }
}
